<template>
  <div class="admin-page">
    <!-- Sidebar Navigation -->
    <aside
      class="admin-sidebar"
      :class="{
        'sidebar-collapsed': isSidebarCollapsed && !isMobileViewport,
        'mobile-open': isMobileMenuOpen,
      }"
    >
      <div class="sidebar-header">
        <div class="sidebar-logo">
          <i class="fas fa-shield-alt"></i>
          <span v-if="!isSidebarCollapsed || isMobileViewport" class="sidebar-title">{{ tr('admin.panelTitle') }}</span>
        </div>
        <button
          @click="isMobileViewport ? closeMobileMenu() : toggleSidebar()"
          class="sidebar-toggle"
          :title="isMobileViewport ? tr('admin.closeMenu') : (isSidebarCollapsed ? tr('admin.expand') : tr('admin.collapse'))"
        >
          <i :class="isMobileViewport ? 'fas fa-times' : (isSidebarCollapsed ? 'fas fa-angle-right' : 'fas fa-angle-left')"></i>
        </button>
      </div>

      <nav class="sidebar-nav">
        <button
          v-for="tab in adminTabs"
          :key="tab.id"
          @click="handleTabSelect(tab.id)"
          :class="['nav-item', { 'active': activeTab === tab.id }]"
          :title="tab.label"
        >
          <i :class="tab.icon"></i>
          <span v-if="!isSidebarCollapsed || isMobileViewport" class="nav-label">{{ tab.label }}</span>
          <span v-if="tab.badge && (!isSidebarCollapsed || isMobileViewport)" class="nav-badge">{{ tab.badge }}</span>
        </button>
      </nav>

      <div class="sidebar-footer">
        <div v-if="!isSidebarCollapsed || isMobileViewport" class="user-info-card">
          <div class="user-avatar">
            <i class="fas fa-user-circle"></i>
          </div>
          <div class="user-details">
            <div class="user-email">{{ user?.email }}</div>
            <div class="user-role">{{ tr('admin.role') }}</div>
          </div>
        </div>
        <button @click="handleLogout" class="logout-button" :title="isSidebarCollapsed && !isMobileViewport ? tr('admin.logout') : ''">
          <i class="fas fa-sign-out-alt"></i>
          <span v-if="!isSidebarCollapsed || isMobileViewport">{{ tr('admin.logout') }}</span>
        </button>
      </div>
    </aside>

    <div
      v-if="isMobileViewport && isMobileMenuOpen"
      class="sidebar-overlay"
      @click="closeMobileMenu"
    ></div>

    <!-- Main Content Area -->
    <div class="admin-main" :class="{ 'main-expanded': isSidebarCollapsed }">
      <header class="content-header">
        <div class="header-left">
          <button
            v-if="isMobileViewport"
            @click="toggleMobileMenu"
            class="mobile-menu-button"
            :aria-label="tr('admin.openMenu')"
          >
            <i class="fas fa-bars"></i>
          </button>
          <div class="header-text">
            <h1 class="page-title">
              <i :class="currentTabIcon"></i>
              {{ currentTabLabel }}
            </h1>
            <p class="page-subtitle">{{ currentTabSubtitle }}</p>
          </div>
        </div>
      </header>

      <div class="content-body">
        <!-- Dashboard Tab -->
        <AdminDashboard
          v-if="activeTab === 'dashboard'"
          :news="news"
          :school-news="schoolNews"
          @navigate="handleTabSelect"
        />

        <!-- News Management Tab -->
        <NewsManagement
          v-if="activeTab === 'news'"
          @open-edit-modal="openEditModal"
          @open-delete-modal="confirmDelete"
        />

        <!-- School Management Tab -->
        <SchoolManagement
          v-if="activeTab === 'school'"
        />

        <!-- School News Management Tab -->
        <SchoolNewsManagement
          v-if="activeTab === 'school-news'"
          @open-edit-school-news-modal="openEditSchoolNewsModal"
        />
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <Modal
      v-model="showDeleteModal"
      :title="tr('admin.deleteTitle')"
      size="sm"
    >
      <p>{{ tr('admin.deleteQuestionStart') }} "<strong>{{ newsToDelete?.title }}</strong>"{{ tr('admin.deleteQuestionEnd') }}</p>
      <p class="modal-warning">{{ tr('admin.deleteWarning') }}</p>

      <template #footer>
        <button @click="showDeleteModal = false" class="cancel-button">
          {{ tr('admin.cancel') }}
        </button>
        <button @click="handleDelete" class="confirm-delete-button" :disabled="!!deleting">
          <span v-if="deleting" class="loading-spinner">
            <i class="fas fa-spinner fa-spin"></i>
          </span>
          <span v-else>
            <i class="fas fa-check"></i>
            {{ tr('admin.accept') }}
          </span>
        </button>
      </template>
    </Modal>

    <!-- Edit News Modal -->
    <Modal
      v-model="showEditModal"
      :title="tr('admin.editNewsTitle')"
      size="xl"
    >
      <form @submit.prevent="handleUpdateNews" class="edit-form">
        <div class="form-group">
          <label for="edit-title" class="form-label">{{ tr('admin.fieldTitle') }}</label>
          <input
            id="edit-title"
            v-model="editNews.title"
            type="text"
            class="form-input"
            :disabled="editing"
            required
          />
        </div>

        <div class="form-group">
          <label for="edit-content" class="form-label">{{ tr('admin.fieldContent') }}</label>
          <QuillEditor
            ref="editEditorRef"
            v-model:content="editNews.content"
            :options="quillOptions"
            :disabled="editing"
            content-type="html"
            class="quill-editor"
          />
        </div>

        <div class="form-group">
          <label for="edit-createdAt" class="form-label">{{ tr('admin.fieldPublishDate') }}</label>
          <input
            id="edit-createdAt"
            v-model="editNews.createdAt"
            type="datetime-local"
            class="form-input"
            :disabled="editing"
            required
          />
        </div>
      </form>

      <template #footer>
        <button @click="showEditModal = false" class="cancel-button" :disabled="editing">
          {{ tr('admin.cancel') }}
        </button>
        <button
          @click="handleUpdateNews"
          class="update-button"
          :disabled="editing || !editNews.title.trim() || !editNews.content.trim()"
        >
          <span v-if="editing" class="loading-spinner">
            <i class="fas fa-spinner fa-spin"></i>
          </span>
          <span v-else>
            <i class="fas fa-save"></i>
            {{ tr('admin.saveChanges') }}
          </span>
        </button>
      </template>
    </Modal>

    <!-- Edit School News Modal -->
    <Modal v-model="showSchoolNewsEditModal" :title="tr('admin.editSchoolNewsTitle')" size="xl">
      <form @submit.prevent="handleUpdateSchoolNews" class="edit-form">
        <div class="form-group">
          <label for="edit-school-news-title" class="form-label">{{ tr('admin.fieldTitle') }}</label>
          <input
            id="edit-school-news-title"
            v-model="editSchoolNews.title"
            type="text"
            class="form-input"
            :disabled="editingSchoolNews"
            required
          />
        </div>

        <div class="form-group">
          <label for="edit-school-news-content" class="form-label">{{ tr('admin.fieldContent') }}</label>
          <QuillEditor
            ref="schoolNewsEditEditorRef"
            v-model:content="editSchoolNews.content"
            :options="quillOptions"
            :disabled="editingSchoolNews"
            content-type="html"
            class="quill-editor"
          />
        </div>

        <div class="form-group">
          <label for="edit-school-news-createdAt" class="form-label">{{ tr('admin.fieldPublishDate') }}</label>
          <input
            id="edit-school-news-createdAt"
            v-model="editSchoolNews.createdAt"
            type="datetime-local"
            class="form-input"
            :disabled="editingSchoolNews"
            required
          />
        </div>
      </form>

      <template #footer>
        <button @click="showSchoolNewsEditModal = false" class="cancel-button" :disabled="editingSchoolNews">
          {{ tr('admin.cancel') }}
        </button>
        <button
          @click="handleUpdateSchoolNews"
          class="update-button"
          :disabled="editingSchoolNews || !editSchoolNews.title.trim() || !editSchoolNews.content.trim()"
        >
          <span v-if="editingSchoolNews" class="loading-spinner">
            <i class="fas fa-spinner fa-spin"></i>
          </span>
          <span v-else>
            <i class="fas fa-save"></i>
            {{ tr('admin.saveChanges') }}
          </span>
        </button>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import { useNews, type NewsItem } from '@/composables/useNews';
import { useSchoolNews, type SchoolNewsItem } from '@/composables/useSchoolNews';
import Modal from '@/components/ui/Modal.vue';
import { QuillEditor } from '@vueup/vue-quill';
import { type TabItem } from '@/components/admin/AdminTabs.vue';
import AdminDashboard from '@/components/admin/AdminDashboard.vue';
import NewsManagement from '@/components/admin/NewsManagement.vue';
import SchoolManagement from '@/components/admin/SchoolManagement.vue';
import SchoolNewsManagement from '@/components/admin/SchoolNewsManagement.vue';

const router = useRouter();
const { user, logout } = useAuth();
const instance = getCurrentInstance();
const $t = instance?.appContext.config.globalProperties.$t;
const tr = (key: string) => ($t ? $t(key) : key);

// Get composables for data
const newsComposable = useNews();
const { updateNews, deleteNews: deleteNewsItem, fetchNews } = newsComposable;
const news = newsComposable.news;

const schoolNewsComposable = useSchoolNews();
const { updateSchoolNews, fetchSchoolNews } = schoolNewsComposable;
const schoolNews = schoolNewsComposable.schoolNews;

// Sidebar state
const isSidebarCollapsed = ref(false);
const activeTab = ref('dashboard');
const hasLoadedNews = ref(false);
const hasLoadedSchoolNews = ref(false);
const isMobileViewport = ref(false);
const isMobileMenuOpen = ref(false);

const toggleSidebar = () => {
  isSidebarCollapsed.value = !isSidebarCollapsed.value;
};

const updateViewportState = () => {
  isMobileViewport.value = window.innerWidth <= 768;
  if (!isMobileViewport.value) {
    isMobileMenuOpen.value = false;
  }
};

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};

const handleTabSelect = (tabId: string) => {
  activeTab.value = tabId;
  if (isMobileViewport.value) {
    closeMobileMenu();
  }
};

const ensureTabDataLoaded = async (tabId: string) => {
  if (tabId === 'dashboard') {
    if (!hasLoadedNews.value) {
      await fetchNews();
      hasLoadedNews.value = true;
    }
    if (!hasLoadedSchoolNews.value) {
      await fetchSchoolNews();
      hasLoadedSchoolNews.value = true;
    }
    return;
  }

  if (tabId === 'news' && !hasLoadedNews.value) {
    await fetchNews();
    hasLoadedNews.value = true;
  }

  if (tabId === 'school-news' && !hasLoadedSchoolNews.value) {
    await fetchSchoolNews();
    hasLoadedSchoolNews.value = true;
  }
};

onMounted(async () => {
  updateViewportState();
  window.addEventListener('resize', updateViewportState);
  await ensureTabDataLoaded(activeTab.value);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateViewportState);
});

watch(activeTab, async (tabId) => {
  await ensureTabDataLoaded(tabId);
});

// Tabs configuration
const adminTabs = computed<TabItem[]>(() => [
  { id: 'dashboard', label: tr('admin.tabDashboard'), icon: 'fas fa-tachometer-alt' },
  { id: 'news', label: tr('admin.tabNews'), icon: 'fas fa-newspaper', badge: news.value.length.toString() },
  { id: 'school', label: tr('admin.tabSchool'), icon: 'fas fa-school' },
  { id: 'school-news', label: tr('admin.tabSchoolNews'), icon: 'fas fa-graduation-cap', badge: schoolNews.value.length.toString() },
]);

// Current tab computed properties
const currentTab = computed(() => adminTabs.value.find(tab => tab.id === activeTab.value));
const currentTabIcon = computed(() => currentTab.value?.icon || '');
const currentTabLabel = computed(() => currentTab.value?.label || '');
const currentTabSubtitle = computed(() => {
  const subtitles: Record<string, string> = {
    'dashboard': tr('admin.subtitleDashboard'),
    'news': tr('admin.subtitleNews'),
    'school': tr('admin.subtitleSchool'),
    'school-news': tr('admin.subtitleSchoolNews')
  };
  return subtitles[activeTab.value] || '';
});

// Modal states
const showDeleteModal = ref(false);
const newsToDelete = ref<NewsItem | null>(null);
const deleting = ref<string | null>(null);

const showEditModal = ref(false);
const editing = ref(false);
const editNews = ref({
  id: '',
  title: '',
  content: '',
  createdAt: '',
});

const showSchoolNewsEditModal = ref(false);
const editingSchoolNews = ref(false);
const editSchoolNews = ref({
  id: '',
  title: '',
  content: '',
  createdAt: '',
});

// Quill editor refs
const editEditorRef = ref<any>(null);
const schoolNewsEditEditorRef = ref<any>(null);

// Quill options for edit modals
const quillOptions = {
  theme: 'snow',
  placeholder: tr('admin.quillPlaceholder'),
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['image'],
      ['link'],
      ['clean']
    ]
  }
};

// Edit modal handlers
const openEditModal = (newsItem: NewsItem) => {
  editNews.value = {
    id: newsItem.id,
    title: newsItem.title,
    content: newsItem.content,
    createdAt: newsItem.createdAt.toISOString().slice(0, 16),
  };
  showEditModal.value = true;
};

const handleUpdateNews = async () => {
  if (!editNews.value.title.trim() || !editNews.value.content.trim()) return;

  editing.value = true;
  const createdAt = editNews.value.createdAt ? new Date(editNews.value.createdAt) : undefined;
  const success = await updateNews(editNews.value.id, editNews.value.title, editNews.value.content, createdAt);

  if (success) {
    showEditModal.value = false;
    editNews.value = { id: '', title: '', content: '', createdAt: '' };
  }

  editing.value = false;
};

// Delete modal handlers
const confirmDelete = (newsItem: NewsItem) => {
  newsToDelete.value = newsItem;
  showDeleteModal.value = true;
};

const handleDelete = async () => {
  if (!newsToDelete.value) return;

  deleting.value = newsToDelete.value.id;
  const success = await deleteNewsItem(newsToDelete.value.id);

  if (success) {
    showDeleteModal.value = false;
    newsToDelete.value = null;
  }

  deleting.value = null;
};

// School news edit modal handlers
const openEditSchoolNewsModal = (newsItem: SchoolNewsItem) => {
  editSchoolNews.value = {
    id: newsItem.id,
    title: newsItem.title,
    content: newsItem.content,
    createdAt: newsItem.createdAt.toISOString().slice(0, 16),
  };
  showSchoolNewsEditModal.value = true;
};

const handleUpdateSchoolNews = async () => {
  if (!editSchoolNews.value.title.trim() || !editSchoolNews.value.content.trim()) return;

  editingSchoolNews.value = true;
  const createdAt = editSchoolNews.value.createdAt ? new Date(editSchoolNews.value.createdAt) : undefined;
  const updated = await updateSchoolNews(
    editSchoolNews.value.id,
    editSchoolNews.value.title,
    editSchoolNews.value.content,
    createdAt,
  );

  if (updated) {
    showSchoolNewsEditModal.value = false;
    editSchoolNews.value = { id: '', title: '', content: '', createdAt: '' };
  }

  editingSchoolNews.value = false;
};

// Logout handler
const handleLogout = async () => {
  await logout();
  router.push('/');
};
</script>

<style scoped>
.admin-page {
  display: flex;
  min-height: 100vh;
  background: var(--color-neutral-100);
  overflow-x: hidden;
}

/* Sidebar Styles */
.admin-sidebar {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: 280px;
  background: linear-gradient(180deg, var(--color-primary-700), var(--color-primary-900));
  color: white;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  z-index: 1000;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.1);
}

.admin-sidebar.sidebar-collapsed {
  width: 80px;
}

.sidebar-header {
  padding: var(--spacing-6) var(--spacing-4);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  min-height: 80px;
}

.sidebar-logo {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  overflow: hidden;
}

.sidebar-logo i {
  font-size: var(--font-size-2xl);
  flex-shrink: 0;
}

.sidebar-title {
  white-space: nowrap;
  opacity: 1;
  transition: opacity 0.2s ease;
}

.sidebar-collapsed .sidebar-title {
  opacity: 0;
  width: 0;
}

.sidebar-toggle {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: var(--spacing-2);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: var(--transition-all);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.sidebar-toggle:hover {
  background: rgba(255, 255, 255, 0.2);
}

.sidebar-nav {
  flex: 1;
  padding: var(--spacing-4) var(--spacing-2);
  overflow-y: auto;
  overflow-x: hidden;
}

.sidebar-nav::-webkit-scrollbar {
  width: 6px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  padding: var(--spacing-3) var(--spacing-4);
  margin-bottom: var(--spacing-2);
  background: transparent;
  border: none;
  border-radius: var(--border-radius-lg);
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: var(--transition-all);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-base);
  width: 100%;
  text-align: left;
  position: relative;
}

.nav-item i {
  font-size: var(--font-size-lg);
  width: 24px;
  flex-shrink: 0;
  text-align: center;
}

.nav-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-collapsed .nav-label {
  display: none;
}

.nav-badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.125rem 0.5rem;
  border-radius: var(--border-radius-full);
  font-size: 0.75rem;
  font-weight: var(--font-weight-semibold);
  min-width: 20px;
  text-align: center;
}

.sidebar-collapsed .nav-badge {
  display: none;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  transform: translateX(4px);
}

.nav-item.active {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.nav-item.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: white;
  border-radius: 0 4px 4px 0;
}

.sidebar-footer {
  padding: var(--spacing-4);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.user-info-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  padding: var(--spacing-3);
  background: rgba(255, 255, 255, 0.1);
  border-radius: var(--border-radius-lg);
  margin-bottom: var(--spacing-3);
}

.user-avatar {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: var(--border-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-avatar i {
  font-size: var(--font-size-xl);
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-email {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
}

.logout-button {
  width: 100%;
  background: rgba(220, 38, 38, 0.2);
  color: white;
  border: 1px solid rgba(220, 38, 38, 0.3);
  padding: var(--spacing-3);
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  transition: var(--transition-all);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
  font-weight: var(--font-weight-medium);
}

.logout-button:hover {
  background: rgba(220, 38, 38, 0.3);
  border-color: rgba(220, 38, 38, 0.5);
}

.sidebar-collapsed .logout-button {
  padding: var(--spacing-2);
}

.sidebar-collapsed .logout-button span {
  display: none;
}

/* Main Content Area */
.admin-main {
  margin-left: 280px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  transition: margin-left 0.3s ease;
}

.admin-main.main-expanded {
  margin-left: 80px;
}

.content-header {
  background: white;
  padding: var(--spacing-6) var(--spacing-8);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid var(--color-neutral-200);
}

.header-left {
  max-width: 1400px;
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-3);
}

.header-text {
  min-width: 0;
}

.mobile-menu-button {
  width: 42px;
  height: 42px;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--color-neutral-300);
  background: white;
  color: var(--color-neutral-700);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: var(--transition-all);
}

.mobile-menu-button:hover {
  background: var(--color-neutral-100);
  border-color: var(--color-neutral-400);
}

.sidebar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1050;
}

.page-title {
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-neutral-900);
  margin: 0 0 var(--spacing-2) 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
}

.page-title i {
  color: var(--color-primary-600);
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-neutral-600);
  margin: 0;
}

.content-body {
  flex: 1;
  padding: var(--spacing-8);
  max-width: 1400px;
  width: 100%;
}

/* Modal Styles */
.modal-warning {
  color: var(--color-error-600);
  font-weight: var(--font-weight-medium);
  margin: var(--spacing-2) 0 0 0;
}

.cancel-button {
  background: var(--color-neutral-100);
  color: var(--color-neutral-700);
  border: 1px solid var(--color-neutral-300);
  padding: var(--spacing-3) var(--spacing-6);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: var(--transition-all);
  font-weight: var(--font-weight-medium);
  min-width: 100px;
}

.cancel-button:hover {
  background: var(--color-neutral-200);
  border-color: var(--color-neutral-400);
}

.cancel-button:disabled {
  background: var(--color-neutral-100);
  cursor: not-allowed;
  opacity: 0.6;
}

.confirm-delete-button {
  background: var(--color-success-600);
  color: white;
  border: 2px solid var(--color-success-600);
  padding: var(--spacing-3) var(--spacing-6);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: var(--transition-all);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
  font-weight: var(--font-weight-medium);
  min-width: 100px;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
}

.confirm-delete-button:hover:not(:disabled) {
  background: var(--color-success-700);
  border-color: var(--color-success-700);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
}

.confirm-delete-button:disabled {
  background: var(--color-neutral-400);
  border-color: var(--color-neutral-400);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.update-button {
  background: var(--color-success-600);
  color: white;
  border: 1px solid var(--color-success-600);
  padding: var(--spacing-3) var(--spacing-6);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: var(--transition-all);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
  font-weight: var(--font-weight-medium);
  min-width: 140px;
}

.update-button:hover:not(:disabled) {
  background: var(--color-success-700);
  border-color: var(--color-success-700);
  transform: translateY(-1px);
}

.update-button:disabled {
  background: var(--color-neutral-400);
  border-color: var(--color-neutral-400);
  cursor: not-allowed;
  transform: none;
}

/* Edit Form Styles */
.edit-form {
  max-height: 75vh;
  overflow-y: auto;
}

.edit-form .form-group {
  margin-bottom: var(--spacing-4);
}

.edit-form .form-label {
  display: block;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-neutral-700);
  margin-bottom: var(--spacing-2);
}

.edit-form .form-input {
  width: 100%;
  padding: var(--spacing-3) var(--spacing-4);
  border: 1px solid var(--color-neutral-300);
  border-radius: var(--border-radius-lg);
  font-size: var(--font-size-base);
  transition: var(--transition-all);
  background: white;
}

.edit-form .form-input:focus {
  outline: none;
  border-color: var(--color-primary-500);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.edit-form .form-input:disabled {
  background: var(--color-neutral-100);
  cursor: not-allowed;
}

/* Quill Editor Styles for Edit Modals */
.edit-form .quill-editor {
  border: 1px solid var(--color-neutral-300);
  border-radius: var(--border-radius-lg);
  background: white;
  width: 100%;
  display: block;
  margin-top: var(--spacing-2);
}

.edit-form .quill-editor:focus-within {
  border-color: var(--color-primary-500);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.edit-form .quill-editor .ql-toolbar {
  border-top-left-radius: var(--border-radius-lg);
  border-top-right-radius: var(--border-radius-lg);
  border-bottom: 1px solid var(--color-neutral-300);
  width: 100% !important;
  box-sizing: border-box;
}

.edit-form .quill-editor .ql-container {
  border-bottom-left-radius: var(--border-radius-lg);
  border-bottom-right-radius: var(--border-radius-lg);
  min-height: 300px;
  width: 100% !important;
  box-sizing: border-box;
}

.edit-form .quill-editor .ql-editor {
  min-height: 300px;
  font-size: var(--font-size-base);
  line-height: 1.6;
  max-width: none;
  width: 100% !important;
  box-sizing: border-box;
}

.edit-form .quill-editor .ql-editor.ql-blank::before {
  color: var(--color-neutral-500);
  font-style: normal;
}

/* Loading spinner */
.loading-spinner {
  font-size: 1rem;
  color: white;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .admin-sidebar {
    width: 80px;
  }

  .sidebar-title,
  .nav-label,
  .nav-badge,
  .user-info-card {
    display: none !important;
  }

  .admin-main {
    margin-left: 80px;
  }
}

@media (max-width: 768px) {
  .admin-sidebar {
    width: min(84vw, 320px);
    height: 100vh;
    position: fixed;
    left: 0;
    top: 0;
    z-index: 1100;
    transform: translateX(-100%);
    transition: transform 0.25s ease;
  }

  .admin-sidebar.mobile-open {
    transform: translateX(0);
  }

  .sidebar-header {
    min-height: 72px;
  }

  .sidebar-nav {
    display: block;
    padding: var(--spacing-4) var(--spacing-2);
    overflow-y: auto;
  }

  .nav-item {
    flex-direction: row;
    padding: var(--spacing-3) var(--spacing-4);
    min-width: 0;
    text-align: left;
  }

  .nav-label {
    display: block;
    font-size: var(--font-size-sm);
  }

  .nav-badge {
    display: inline-flex;
  }

  .sidebar-footer {
    display: block;
  }

  .admin-main {
    margin-left: 0;
    margin-top: 0;
  }

  .content-header {
    padding: var(--spacing-4);
  }

  .page-title {
    font-size: var(--font-size-2xl);
  }

  .content-body {
    padding: var(--spacing-4);
  }

  .page-subtitle {
    display: none;
  }

  .edit-form {
    max-height: 70vh;
  }

  .edit-form .quill-editor .ql-editor {
    min-height: 250px;
  }
}

@media (max-width: 640px) {
  .page-title {
    font-size: var(--font-size-xl);
  }

  .content-body {
    padding: var(--spacing-3);
  }

  .edit-form .quill-editor .ql-editor {
    min-height: 200px;
  }
}
</style>
